import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { sectorBySlug, sectors } from "@/lib/data/sectors";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { ArrowUpRight } from "lucide-react";

const HERO_IMAGES = {
  "mining-security":
    "https://images.unsplash.com/photo-1655549136009-6aa6ff5de57e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBpbmR1c3RyaWFsJTIwc2l0ZXxlbnwwfHx8fDE3ODUxMDU3MjB8MA&ixlib=rb-4.1.0&q=85",
  "mobile-patrol":
    "https://images.unsplash.com/photo-1680714246510-94d1055e4a8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "event-security":
    "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
  "corporate-security":
    "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
};
const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1579454577332-6c8a8f99a23c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDQ2NDJ8MHwxfHNlYXJjaHw0fHx3aW5uaXBlZyUyMHNreWxpbmUlMjB3aW50ZXJ8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function SectorDetail() {
  const { slug } = useParams();
  const sector = sectorBySlug(slug);

  useSEO({
    title: sector
      ? `${sector.heading} — Manitoba Security Services`
      : "Sector — Manitoba Security Services",
    description: sector ? sector.lede : undefined,
    keywords: sector ? sector.keywords : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/sectors/${slug}`
        : `/sectors/${slug}`,
    jsonLd: sector
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: sector.service?.name,
          provider: {
            "@type": "SecurityService",
            name: "Manitoba Security Services",
            areaServed: "Manitoba, Canada",
          },
          areaServed: {
            "@type": "City",
            name: sector.city?.name,
            containedInPlace: { "@type": "State", name: "Manitoba" },
          },
          description: sector.lede,
        }
      : undefined,
  });

  if (!sector) return <Navigate to="/sectors" replace />;

  const heroImg = HERO_IMAGES[sector.serviceSlug] || DEFAULT_HERO;
  const otherSectors = sectors.filter((s) => s.slug !== sector.slug).slice(0, 3);

  return (
    <div className="bg-[#050505] pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative min-h-[85svh] w-full overflow-hidden border-b border-[#1e1e1e]">
        <img
          src={heroImg}
          alt={sector.heading}
          className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 opacity-55"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 65% 50% at 30% 45%, transparent 0%, rgba(5,5,5,0.9) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-24 md:pt-32 pb-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Sector Program
            </p>
            <div className="mt-6 space-y-3">
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Discipline
                </p>
                <Link
                  to={`/services/${sector.serviceSlug}`}
                  className="font-display uppercase tracking-tight text-white text-base link-underline"
                >
                  {sector.service?.name}
                </Link>
              </div>
              <div>
                <p className="font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-[10px]">
                  Community
                </p>
                <Link
                  to={`/cities/${sector.citySlug}`}
                  className="font-display uppercase tracking-tight text-white text-base link-underline"
                >
                  {sector.city?.name}
                </Link>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <RevealText
              lines={sector.heading.split(". ").map((s, i, a) => (i < a.length - 1 ? s + "." : s))}
              className="font-display font-black uppercase text-white tracking-tightest leading-[0.9] text-[clamp(2.5rem,7.5vw,7rem)]"
              stagger={0.1}
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
            >
              {sector.lede}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32 space-y-20 md:space-y-24">
        {sector.body.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Chapter {String(i + 1).padStart(2, "0")}
              </p>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* SEO keyword band */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-12">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {sector.keywords.map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related sectors */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-24">
        <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
          Continue reading — other sector programs
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {otherSectors.map((s) => (
            <Link
              key={s.slug}
              to={`/sectors/${s.slug}`}
              data-testid={`related-sector-${s.slug}`}
              className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-8 transition-colors block"
            >
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                {s.service?.number}
              </p>
              <h4 className="mt-2 font-display uppercase tracking-tight text-white text-lg md:text-xl leading-tight group-hover:translate-x-1 transition-transform">
                {s.heading}
              </h4>
              <p className="mt-3 font-editorial italic text-[#a0a0a0] text-sm">
                {s.lede}
              </p>
              <ArrowUpRight
                className="mt-4 text-white/60 group-hover:text-[#FF3B30]"
                size={20}
              />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
